/**
 * TypeScript types for the adventures system.
 */

/**
 * Character health information for adventures.
 */
export interface CharacterHealth {
    current_health: number;
    max_health: number;
    health_percentage: number;
    is_incapacitated: boolean;
}

/**
 * Adventure challenge - a stat or skill check with a difficulty.
 */
export interface AdventureChallenge {
    id: number;
    stat_type: string;
    difficulty: number;
    position: number;
    adventure_stage_id?: number | null;
}

/**
 * Reward type with label and icon.
 */
export interface RewardType {
    value: 'item' | 'blueprint' | 'material' | 'xp' | 'reputation';
    label: string;
    icon: string;
}

/**
 * Item rarity with label.
 */
export interface ItemRarity {
    value: string;
    label: string;
}

/**
 * Item recipe reference.
 */
export interface ItemRecipeRef {
    id: number;
    name: string;
}

/**
 * Adventure reward - potential reward from completing an adventure.
 */
export interface AdventureReward {
    id: number;
    reward_type: RewardType;
    item_recipe: ItemRecipeRef | null;
    item_rarity: ItemRarity | null;
    material_type: string | null;
    enclave_id: number | null;
    enclave: { id: number; name: string; owner_name: string | null } | null;
    reputation_min: number | null;
    reputation_max: number | null;
    quantity_min: number;
    quantity_max: number;
    chance_percent: number;
    position: number;
    description: string | null;
    tags: { id: number; name: string; slug: string }[];
    adventure_stage_id?: number | null;
}

/**
 * Adventure stage - one stage in a multi-stage adventure.
 */
export interface AdventureStage {
    id: number;
    stage_number: number;
    dialogue_pre: string | null;
    dialogue_success: string | null;
    dialogue_failure: string | null;
    challenges: AdventureChallenge[];
    rewards: AdventureReward[];
}

/**
 * Adventure media (image).
 */
export interface AdventureMedia {
    id: number;
    urls: {
        small: string;
        medium: string;
        large: string;
    };
    alt_text: string | null;
}

/**
 * Adventure - a quest that characters can attempt.
 */
export interface Adventure {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    dialogue_start: string | null;
    dialogue_success: string | null;
    dialogue_failure: string | null;
    is_active: boolean;
    /** Difficulty modifier applied to all challenges (-5 to +5) */
    difficulty_modifier: number;
    total_difficulty: number;
    /** Challenges for single-stage adventures (null stage_id) */
    challenges: AdventureChallenge[];
    /** Rewards for single-stage adventures (null stage_id) */
    rewards: AdventureReward[];
    /** Whether this is a multi-stage adventure */
    is_multi_stage: boolean;
    /** Number of stages (1 for single-stage) */
    stage_count: number;
    /** Stages for multi-stage adventures */
    stages?: AdventureStage[];
    attempts_count: number;
    created_by: string | null;
    created_at: string;
    /** Estimated time to complete in minutes (total_difficulty * 10) */
    estimated_minutes?: number;
    /** Media attached to the adventure */
    media?: AdventureMedia[];
}

/**
 * Status of an adventure attempt.
 */
export interface AdventureAttemptStatus {
    value: 'pending' | 'in_progress' | 'success' | 'partial' | 'failure';
    label: string;
    icon: string;
    tailwindClass: string;
}

/**
 * Outcome of an adventure roll.
 */
export interface AdventureOutcome {
    value: 'failure' | 'partial_success' | 'success' | 'critical_success';
    label: string;
    icon: string;
    tailwindClass: string;
}

/**
 * Character participating in an adventure attempt.
 */
export interface AdventureAttemptCharacter {
    id: number;
    name: string;
    hex_id: number;
    xp_awarded: number;
    /** Health information for the character */
    health?: CharacterHealth;
}

/**
 * Stage attempt status.
 */
export interface StageAttemptStatus {
    value: 'pending' | 'in_progress' | 'success' | 'partial' | 'failure';
    label: string;
}

/**
 * Rolling character information for stage outcomes.
 */
export interface RollingCharacter {
    id: number;
    name: string;
    pronouns: string | null;
}

/**
 * Result of a single challenge roll within a stage.
 */
export interface ChallengeResult {
    challenge_id: number;
    stat_type: string;
    difficulty: number;
    character_id: number | null;
    character_name: string | null;
    character_pronouns: string | null;
    character_score: number;
    success_chance: number;
    roll: number;
    is_doubles: boolean;
    outcome: 'failure' | 'partial_success' | 'success' | 'critical_success';
}

/**
 * Adventure stage attempt - tracking a single stage in a multi-stage adventure.
 */
export interface AdventureStageAttempt {
    id: number;
    adventure_attempt_id: number;
    adventure_stage_id: number;
    stage_number: number;
    status: StageAttemptStatus;
    roll_result: number | null;
    success_chance: number | null;
    is_critical: boolean;
    /** The character who made the roll */
    rolling_character: RollingCharacter | null;
    /** The skill used for the roll (e.g., 'combat', 'magic') */
    rolling_skill: string | null;
    /** The character's skill value when rolling */
    rolling_skill_value: number | null;
    /** Per-challenge roll results (multi-stage only) */
    challenge_results: ChallengeResult[] | null;
    rewards_granted: GrantedReward[] | null;
    damage_dealt: Record<number, number> | null;
    healing_applied: {
        passive: Record<number, number>;
        items: { character_id: number; item_name: string; amount: number }[];
    } | null;
    started_at: string | null;
    completed_at: string | null;
    completes_at: string | null;
    /** Remaining seconds until stage completes */
    remaining_seconds: number;
    /** Stage dialogue and challenges */
    stage?: AdventureStage;
}

/**
 * Granted reward from a completed adventure.
 */
export interface GrantedReward {
    type: 'item' | 'blueprint' | 'material' | 'xp' | 'reputation';
    // Item/Blueprint rewards
    item_id?: number;
    item_name?: string;
    quality?: number;
    // Material rewards
    material_type?: string;
    quantity?: number;
    // XP rewards
    xp_per_character?: number;
    total_xp?: number;
    character_xp?: Record<number, number>;
    // Reputation rewards
    enclave_id?: number;
    enclave_name?: string;
    amount?: number;
    old_value?: number;
    new_value?: number;
    new_level?: string;
}

/**
 * Adventure attempt - a user's attempt at an adventure.
 */
export interface AdventureAttempt {
    id: number;
    adventure: Adventure;
    status: AdventureAttemptStatus;
    roll_result: number | null;
    success_chance: number | null;
    is_critical: boolean;
    outcome: AdventureOutcome | null;
    rewards_granted: GrantedReward[] | null;
    rewards_claimed: boolean;
    characters: AdventureAttemptCharacter[];
    started_at: string | null;
    completed_at: string | null;
    completes_at: string | null;
    /** Remaining seconds until adventure completes (0 if ready) */
    remaining_seconds: number;
    /** Whether the adventure is ready to complete */
    is_ready_to_complete: boolean;
    /** Multi-stage adventure fields */
    is_multi_stage: boolean;
    current_stage: number;
    total_stages: number;
    /** Stage attempts for multi-stage adventures */
    stage_attempts?: AdventureStageAttempt[];
    /** Current stage attempt (if in progress) */
    current_stage_attempt?: AdventureStageAttempt | null;
    /** Accumulated rewards from completed stages (before final claim) */
    accumulated_rewards?: GrantedReward[] | null;
    /** Whether ready for healing phase between stages */
    is_ready_for_healing?: boolean;
}

/**
 * Character available for adventure selection.
 */
export interface AvailableCharacter {
    id: number;
    name: string;
    level: number;
    class: string;
    stats: Record<string, number>;
    skills: Record<string, number>;
    equipment_bonuses: Record<string, number>;
    /** Health information for the character */
    health?: CharacterHealth;
}

/**
 * Healing item available in the vault.
 */
export interface HealingItem {
    id: number;
    name: string;
    healing_amount: number;
    quality: number;
    rarity: {
        value: string;
        label: string;
    };
}

/**
 * Character preview for healing phase.
 */
export interface HealingCharacterPreview {
    id: number;
    name: string;
    current_health: number;
    max_health: number;
    health_percentage: number;
    is_incapacitated: boolean;
    would_heal: number;
}

/**
 * Healing phase preview data.
 */
export interface HealingPhasePreview {
    passive_per_character: number;
    characters: HealingCharacterPreview[];
}

/**
 * Props for the HealingPhase page.
 */
export interface HealingPhaseProps {
    attempt: AdventureAttempt;
    healingPreview: HealingPhasePreview;
    healingItems: HealingItem[];
    stageAttempt: AdventureStageAttempt;
}

/**
 * Format minutes into a human-readable duration string.
 */
export function formatDuration(minutes: number): string {
    if (minutes < 60) {
        return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
        return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}m`;
}

